import { getConversation_conversations_data_messages } from '../../../__generated__/getConversation';
import { ProductTypeEnum } from '../../../__generated__/globalTypes';
import CanvasLayoutStyle from '../../style_detail/canvas_layout_style';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SuggestionsButtons from './SuggestionsButtons';
import { STYLE_CARD_SUBTRACT_HEIGHT } from '../../../constants';
import { useVisualViewport } from '../../../utils/useVisaulViewport';
import useDynamicItemDetail from '../hooks/useDynamicItemDetail';
import useMessageContainerRef from '../hooks/useMessageContainerRef';
import langUtils from '../../language/utils';
import { useStyles } from '../styles';
import { Collapse } from 'react-collapse';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import useEnableVtonFeature from '../../../hooks/useEnableVtonFeature';
import { VtonCard } from 'component/vton_card/VtonCard';
import { useChatContext } from '../hooks/ChatContext';

const SuggestionsSingleSlide = ({
    message,
    messageIndex,
    openedInfoMessage,
    setOpenedInfoMessage,
}: {
    message: getConversation_conversations_data_messages;
    messageIndex: number;
    openedInfoMessage: string | null;
    setOpenedInfoMessage: (value: string | null) => void;
}) => {
    const { localUser } = useChatContext();
    const classes = useStyles();
    const windowSize = useVisualViewport();
    const { setShowDynamicItemDetail } = useDynamicItemDetail();
    const messageContainerRef = useMessageContainerRef();
    const descriptionRef = useRef<HTMLDivElement>(null);
    const elements = message.elements;
    const element = elements && Array.isArray(elements) && elements.length ? elements[0] : null;
    const isOpenInfo = message.taffiId && openedInfoMessage?.includes(message.taffiId);
    const userDetail = useSelector((state: RootState) => state.userDetail.me);
    const enableVtonFeature = useEnableVtonFeature();
    const [vtonSliderPercentage, setVtonSliderPercentage] = useState<number>(99);

    const styleCardHeight = useMemo(() => {
        return windowSize.viewport.height - STYLE_CARD_SUBTRACT_HEIGHT;
    }, [windowSize.viewport.height]);

    const renderStyleCard = useCallback(() => {
        if (!windowSize.viewport.height) {
            return null;
        }
        if (!styleCardHeight) {
            return null;
        }
        const productType = element?.productType;
        if (productType === ProductTypeEnum.item) {
            return enableVtonFeature ? (
                <VtonCard
                    height={styleCardHeight}
                    item={element}
                    userDetail={userDetail ?? localUser}
                    canvasStyle={
                        <img
                            src={element?.value!!}
                            alt="style"
                            className={classes.styleImage}
                            style={{
                                width: '100%',
                                height:
                                    windowSize.viewport.width >= 1024
                                        ? 630
                                        : windowSize.viewport.height - STYLE_CARD_SUBTRACT_HEIGHT,
                            }}
                            onClick={() => {
                                let item: any = element?.items?.find(
                                    (data) => data?.taffiId === element?.productTaffiId,
                                );
                                if (item?.images?.length) {
                                    item = {
                                        ...item,
                                        images: [element?.value, ...item.images],
                                        storeUrl: element?.url,
                                    };
                                }
                                if (element?.items?.length) {
                                    setShowDynamicItemDetail(item);
                                }
                            }}
                        />
                    }
                    externalSliderPercentage={vtonSliderPercentage}
                    setVtonSliderPercentage={setVtonSliderPercentage}
                />
            ) : (
                <img
                    src={element?.value!!}
                    alt="style"
                    className={classes.styleImage}
                    style={{
                        width: '100%',
                        height:
                            windowSize.viewport.width >= 1024
                                ? 630
                                : windowSize.viewport.height - STYLE_CARD_SUBTRACT_HEIGHT,
                    }}
                    onClick={() => {
                        let item: any = element?.items?.find((data) => data?.taffiId === element?.productTaffiId);
                        if (item?.images?.length) {
                            item = { ...item, images: [element?.value, ...item.images], storeUrl: element?.url };
                        }
                        if (element?.items?.length) {
                            setShowDynamicItemDetail(item);
                        }
                    }}
                />
            );
        }
        const items = element?.items?.map((i: any) => {
            return {
                ...i,
                metadata: {
                    category: i?.data?.metadata?.category || i?.category,
                },
            };
        });
        return enableVtonFeature ? (
            <VtonCard
                height={styleCardHeight}
                style={element}
                userDetail={userDetail ?? localUser}
                canvasStyle={
                    <CanvasLayoutStyle
                        items={items ?? []}
                        heightContent={
                            windowSize.viewport.width >= 1024
                                ? 630
                                : windowSize.viewport.height - STYLE_CARD_SUBTRACT_HEIGHT
                        }
                        style={element}
                        setShowDynamicItemDetail={setShowDynamicItemDetail}
                    />
                }
                externalSliderPercentage={vtonSliderPercentage}
                setVtonSliderPercentage={setVtonSliderPercentage}
            />
        ) : (
            <CanvasLayoutStyle
                items={items ?? []}
                heightContent={styleCardHeight}
                style={element}
                setShowDynamicItemDetail={setShowDynamicItemDetail}
            />
        );
    }, [
        classes.styleImage,
        element,
        enableVtonFeature,
        localUser,
        setShowDynamicItemDetail,
        styleCardHeight,
        userDetail,
        vtonSliderPercentage,
        windowSize.viewport.height,
        windowSize.viewport.width,
    ]);

    const getDescription = useMemo(() => {
        if (!message.content || !message.taffiId) {
            return '';
        }
        return message.content.replace(/[^:]+(: )/, '');
    }, [message]);

    const renderDescription = () => ({ __html: getDescription });

    useEffect(() => {
        if (isOpenInfo && descriptionRef.current && messageContainerRef.current) {
            try {
                // check if descriptionRef.current is totally visible, if not, scroll to it
                const { bottom } = descriptionRef.current.getBoundingClientRect();
                if (bottom > messageContainerRef.current.clientHeight + 60) {
                    messageContainerRef.current.scrollTo({
                        top:
                            messageContainerRef.current.scrollTop +
                            (bottom - messageContainerRef.current.clientHeight - 60), // 60 is the height of the top bar
                        behavior: 'smooth',
                    });
                }
            } catch (e) {
                console.error(e);
            }
        }
    }, [descriptionRef, element, isOpenInfo, messageContainerRef]);

    return (
        <div style={{ height: '100%' }}>
            {renderStyleCard()}
            <SuggestionsButtons
                message={message}
                messageIndex={messageIndex}
                openedInfoMessage={openedInfoMessage}
                setOpenedInfoMessage={setOpenedInfoMessage}
                vtonSliderPercentage={vtonSliderPercentage}
                setVtonSliderPercentage={setVtonSliderPercentage}
            />
            <Collapse isOpened={isOpenInfo}>
                <div
                    ref={descriptionRef}
                    className={'suggestions-details'}
                    style={{
                        ...langUtils.getLanguageStyle(message?.language),
                    }}
                >
                    <span className={`suggestions-description`} dangerouslySetInnerHTML={renderDescription()}></span>
                </div>
            </Collapse>
        </div>
    );
};

export default SuggestionsSingleSlide;
